/* eslint-disable @next/next/no-html-link-for-pages */
import { UseFormReturn } from "react-hook-form";
import { LegalAcknowledgementType } from "../../../modules/legal/models";
import tenantConfig from "../../../tenant.config";
import { ErrorMessageComponent } from "../../Common/Input/ErrorMessage/ErrorMessage";
import PasswordFieldComponent from "../../Common/Input/Password/Password";
import { PrimeButton } from "../../prime/button";
import { SignupFormSchemaType } from "./SignupFormSchema";
import { PrimeLink } from "../../prime/link";
import { featureFlag } from "../../../lib/featureFlags/featureFlags";

interface AuthSignupComponentProps {
  title?: string;
  info?: string;
  useFormProps: UseFormReturn<SignupFormSchemaType>;
  isFetching: boolean;
  onSubmit: (data: any) => Promise<void>;
  tncs?: {
    url: string;
    title: string;
    type: string;
  }[];
  currentCountry: string;
  componentId?: string;
}

function getTnCUrlAndTitle(
  tncs:
    | {
        url: string;
        title: string;
        type: string;
      }[]
    | undefined,
  target: string,
  defaultTitle: string
) {
  if (!tncs) {
    return {
      url: tenantConfig.entity_legal_url,
      title: defaultTitle,
    };
  }
  return {
    url:
      tncs.find((tnc) => tnc.type.includes(target))?.url ||
      tenantConfig.entity_legal_url,
    title: tncs.find((tnc) => tnc.type.includes(target))?.title || defaultTitle,
  };
}

function renderLegalLink(
  tncs: {
    url: string;
    title: string;
    type: string;
  }[],
  target: string,
  defaultTitle: string
) {
  let result = getTnCUrlAndTitle(tncs, target, defaultTitle);
  return (
    <PrimeLink
      id={"signup-form-link-" + target}
      href={result.url}
      nextLink
      newTab
    >
      {result.title}
    </PrimeLink>
  );
}

function renderLegalCopy(
  tncs: {
    url: string;
    title: string;
    type: string;
  }[],
  currentCountry: string
) {
  let part1 = null;
  let part2 = null;
  switch (currentCountry) {
    case "MY":
      part1 = (
        <>
          By accessing the {tenantConfig.entity_short_name} platform, you agree
          to our{" "}
          {renderLegalLink(
            tncs,
            LegalAcknowledgementType.MY_TERMS_OF_USE,
            "Terms Of Use"
          )}{" "}
          and{" "}
          {renderLegalLink(
            tncs,
            LegalAcknowledgementType.MY_PRIVACY_POLICY,
            "Privacy Policy"
          )}
          . As an investor, you also acknowledge that investing carries risks,
          including loss of capital. Please consider and read our{" "}
          {renderLegalLink(
            tncs,
            LegalAcknowledgementType.MY_RISK_WARNING_DISCLAIMERS,
            "Risk Warning"
          )}{" "}
          in full.
        </>
      );
      part2 = (
        <>
          Receive our electronic newsletter and other occasional updates, in
          accordance to our{" "}
          {renderLegalLink(
            tncs!,
            LegalAcknowledgementType.MY_PRIVACY_POLICY,
            "Privacy Policy"
          )}
          .
        </>
      );
      break;
    default:
      part1 = (
        <>
          By accessing the {tenantConfig.entity_short_name} platform, you agree
          to our{" "}
          {renderLegalLink(
            tncs,
            LegalAcknowledgementType.SG_TERMS_OF_USE,
            "Terms of Use"
          )}{" "}
          and{" "}
          {renderLegalLink(
            tncs,
            LegalAcknowledgementType.SG_PRIVACY_POLICY,
            "Privacy Policy"
          )}{" "}
          . As an investor, you also acknowledge that investing carries risks,
          including loss of capital. Please consider and read our{" "}
          {renderLegalLink(
            tncs,
            LegalAcknowledgementType.SG_RISK_DISCLOSURES,
            "Risk Disclosures"
          )}{" "}
          in full.
        </>
      );
      part2 = (
        <>
          Receive our electronic newsletter and other occasional updates, in
          accordance to our{" "}
          {renderLegalLink(
            tncs!,
            LegalAcknowledgementType.SG_PRIVACY_POLICY,
            "Privacy Policy"
          )}
          .
        </>
      );
      break;
  }
  return { part1, part2 };
}

export const AuthSignupComponent = (props: AuthSignupComponentProps) => {
  const { info, onSubmit, useFormProps, isFetching, tncs, currentCountry } =
    props;

  const { part1, part2 } = renderLegalCopy(tncs!, currentCountry);

  return (
    <form id={props.componentId} onSubmit={useFormProps.handleSubmit(onSubmit)}>
      <div className="space-y-5 sm:space-y-6">
        {!!info ? (
          <div className="mt-10 mb-6 bg-cyan-50/70 sm:mt-7">
            <p className="p-2 text-center text-sm font-light text-gray-600">
              {info}
            </p>
          </div>
        ) : (
          <></>
        )}
        <div
          className="grid grid-cols-1 grid-rows-2 gap-y-6 
                        sm:grid-cols-2 sm:grid-rows-1 sm:gap-x-4"
        >
          <div className="relative sm:col-span-1">
            <input
              id={props.componentId + "-input-first-name"}
              type="text"
              className={`fp-text-input static ${
                useFormProps.formState.errors.firstName && `border-red-700`
              }`}
              placeholder="First Name"
              autoComplete="given-name"
              {...useFormProps.register("firstName")}
              disabled={isFetching}
            />
            <ErrorMessageComponent
              componentId={props.componentId + "-input-first-name"}
              fieldError={useFormProps.formState.errors.firstName}
            />
          </div>
          <div className="relative sm:col-span-1">
            <input
              id={props.componentId + "-input-last-name"}
              type="text"
              className={`fp-text-input static ${
                useFormProps.formState.errors.lastName && `border-red-700`
              }`}
              placeholder="Last Name"
              autoComplete="family-name"
              {...useFormProps.register("lastName")}
              disabled={isFetching}
            />
            <ErrorMessageComponent
              componentId={props.componentId + "-input-last-name"}
              fieldError={useFormProps.formState.errors.lastName}
            />
          </div>
          <div className="relative col-span-1 sm:col-span-2">
            <input
              id={props.componentId + "-input-email"}
              type="text"
              className={`fp-text-input static ${
                useFormProps.formState.errors.email && `border-red-700`
              }`}
              placeholder="Email"
              autoComplete="email"
              {...useFormProps.register("email")}
              disabled={isFetching}
            />
            <label htmlFor="email">
              Please use a corporate email if you are signing up as an
              organisation investor.
            </label>
            <ErrorMessageComponent
              componentId={props.componentId + "-input-email"}
              fieldError={useFormProps.formState.errors.email}
            />
          </div>
          <div className="relative col-span-1 sm:col-span-2">
            <PasswordFieldComponent
              componentId={props.componentId}
              fieldError={useFormProps.formState.errors.password}
              register={useFormProps.register}
              name="password"
              showCheckboxes={true}
              disabled={isFetching}
            />
          </div>
        </div>
      </div>
      <div className="mt-10 mb-7 sm:mt-7">
        <p className="font-base text-sm font-light tracking-wide text-slate-900">
          {part1}
        </p>
        {featureFlag("marketingOptIn") && (
          <label className="h-4 cursor-pointer">
            <input
              id={props.componentId + "-checkbox-tnc"}
              type="checkbox"
              className="mr-2 mt-5 h-4 w-4 translate-y-1 cursor-pointer"
              {...useFormProps.register("marketingPreferences")}
            />
            {part2}
          </label>
        )}
      </div>

      <PrimeButton
        size="large"
        id={props.componentId + "-submit-button"}
        type="submit"
        status={isFetching ? "loading" : "default"}
        loadingText="Creating your account..."
      >
        Create account
      </PrimeButton>
    </form>
  );
};
